'use client'

import { cn } from '@onclicks//utilities/ui'
import { Anchor } from '@onclicks/payload-types'
import { CMSLinkType } from '@onclicks/types/CMSLink'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React from 'react'
import { Button, type ButtonProps } from './ui/Button'
import { Icon } from './Icon'

export const CMSLink: React.FC<CMSLinkType<ButtonProps['variant'], ButtonProps['size']>> = (
  props,
) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    reactIconsIcon,
    url,
  } = props

  const pathname = usePathname()

  let href: string | undefined | null = url

  href = getHref()

  if (!href) return null

  const isAnchor = href.includes('#')
  const size = appearance === 'link' ? 'clear' : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  // Button autour de a et Link = lien ressemble à un bouton graphique, propriété asChild transmet toutes ses propriétés CSS à ses enfants
  // apparence inline = apparence simple, texte standard
  if (isAnchor) {
    if (appearance === 'inline') {
      return (
        <a href={href} className={cn(className)}>
          <Icon reactIconsIcon={reactIconsIcon} className="" />
          {label}
          {children}
        </a>
      )
    } else {
      return (
        <Button asChild className={className} size={size} variant={appearance}>
          <a href={href} className={cn(className)}>
            <Icon reactIconsIcon={reactIconsIcon} className="" />
            {label}
            {children}
          </a>
        </Button>
      )
    }
  } else {
    if (appearance === 'inline') {
      return (
        <Link className={cn(className)} href={href} {...newTabProps}>
          <Icon reactIconsIcon={reactIconsIcon} className="" />
          {label && label}
          {children && children}
        </Link>
      )
    } else {
      return (
        <Button asChild className={className} size={size} variant={appearance}>
          <Link className={cn(className)} href={href} {...newTabProps}>
            <Icon reactIconsIcon={reactIconsIcon} className="" />
            {label && label}
            {children && children}
          </Link>
        </Button>
      )
    }
  }

  function getHref() {
    if (type === 'reference' && reference?.value && typeof reference.value === 'object') {
      if (reference.relationTo === 'anchors') {
        // Pour Anchors
        const anchorDoc = reference.value as Anchor
        const anchorSlug = anchorDoc.anchor.replace(/^#/, '')
        const anchorPage =
          anchorDoc.page && typeof anchorDoc.page === 'object' ? anchorDoc.page : null

        if (anchorPage?.slug) {
          // L'ancre est explicitement rattachée à une page : on construit un lien absolu vers celle-ci
          const pagePath = anchorPage.slug === 'home' ? '' : `/${anchorPage.slug}`
          return `${pagePath}/#${anchorSlug}`
        }

        // Pas de page associée : on considère que l'ancre se trouve sur la page actuellement affichée
        const currentPath = pathname === '/' ? '' : pathname
        return `${currentPath}#${anchorSlug}`
      } else if (
        'slug' in reference.value &&
        reference.value.slug !== null &&
        reference.value.slug !== undefined
      ) {
        // Pour Pages / Posts
        return `${reference.relationTo !== 'pages' ? `/${reference.relationTo}` : ''}/${reference.value.slug}`
      }
    }

    if (type === 'custom' && url) {
      return url
    }
    return null
  }
}
